import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import FormSection from "../../../../common/components/functional/formSection/FormSection";
import { EditEmploymentAnnualHolidayAccumulationRule, Employment } from "../../../employment/employmentApi";
import { editEmpAnnualHolidayAccumulationRule } from "../../../employment/employmentSlice";
import { SingleEditFormRow } from "./SingleEditFormRow";

type Props = {
  employment: Employment;
  canEdit: boolean;
};

type FormData = { annualHolidayAccumulationRule: string };

export const HolidayInfo = ({ employment, canEdit }: Props) => {
  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();

  const accumulationRules = useAppSelector(
    (state) => state.localized.annualHolidayAccumulationRule,
  );
  const accumulationRuleOptions = Object.keys(accumulationRules).map((key) => ({
    id: key,
    name: accumulationRules[key],
    value: key,
  }));

  const annualHolidayAccumulationRule = employment?.details?.annualHolidayAccumulationRule || "";
  const defaultValues = {
    annualHolidayAccumulationRule,
  };

  const methods = useForm({ defaultValues, mode: "onChange" });
  const {
    getValues,
    reset,
    formState: { isSubmitted },
    handleSubmit,
  } = methods;

  useEffect(() => {
    if (isSubmitted) {
      reset(getValues());
    }
  }, [isSubmitted, reset, getValues]);

  useEffect(() => {
    reset({ annualHolidayAccumulationRule });
  }, [annualHolidayAccumulationRule, reset]);

  const onSubmit = (data: FormData) => {
    const payload: EditEmploymentAnnualHolidayAccumulationRule = {
      employmentId: employment?.employmentId,
      version: employment?.details.version,
      annualHolidayAccumulationRule: data.annualHolidayAccumulationRule
    };
    dispatch(editEmpAnnualHolidayAccumulationRule(payload));
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
    reset();
  };

  return (
    <FormSection title={t("holidayInfo")}>
      <FormProvider {...methods}>
        <SingleEditFormRow
          name="annualHolidayAccumulationRule"
          label={t("talentProfile:annualHolidayAccumulationRule")}
          rules={{}}
          canEdit={canEdit}
          onSave={onSave}
          type="Select"
          localization={accumulationRules}
          selectOptions={accumulationRuleOptions}
        />
      </FormProvider>
    </FormSection>
  );
};
